import { ParsedCSVRow } from '../../types/budget';

const DATE_FORMATS = [
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
  /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
  /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
  /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/,
];

const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  result.push(current.trim());
  return result;
};

export const parseCSV = (csvText: string): { headers: string[]; rows: ParsedCSVRow[] } => {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());

  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = parseCsvLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    const row: ParsedCSVRow = {};

    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });

    return row;
  });

  return { headers, rows };
};

export const parseDate = (dateStr: string): string | null => {
  for (const format of DATE_FORMATS) {
    const match = dateStr.match(format);
    if (!match) {
      continue;
    }

    let year: number;
    let month: number;
    let day: number;

    if (format === DATE_FORMATS[2]) {
      year = parseInt(match[1], 10);
      month = parseInt(match[2], 10);
      day = parseInt(match[3], 10);
    } else if (format === DATE_FORMATS[3]) {
      month = parseInt(match[1], 10);
      day = parseInt(match[2], 10);
      year = 2000 + parseInt(match[3], 10);
    } else {
      month = parseInt(match[1], 10);
      day = parseInt(match[2], 10);
      year = parseInt(match[3], 10);
    }

    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900 && year <= 2100) {
      return new Date(year, month - 1, day).toISOString().split('T')[0];
    }
  }

  const date = new Date(dateStr);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }

  return null;
};

export const parseAmount = (amountStr: string, invert = false): number | null => {
  if (!amountStr || amountStr.trim() === '') {
    return null;
  }

  const isNegative = amountStr.includes('(') && amountStr.includes(')');

  let cleaned = amountStr
    .replace(/[$£€¥]/g, '')
    .replace(/,/g, '')
    .replace(/\(/g, '')
    .replace(/\)/g, '')
    .trim();

  const hasNegativeSign = cleaned.startsWith('-');
  cleaned = cleaned.replace(/-/g, '');

  const amount = parseFloat(cleaned);
  if (Number.isNaN(amount)) {
    return null;
  }

  let finalAmount = isNegative || hasNegativeSign ? -Math.abs(amount) : amount;
  if (invert) {
    finalAmount = -finalAmount;
  }

  return finalAmount;
};
